import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import {
  Loader2,
  Mail,
  ArrowLeft,
  Store,
  Sparkles,
  CheckCircle,
  Key,
  Shield,
  Lock,

  CheckCircle2,
  Clock,
  Zap,
} from "lucide-react";
import { authService } from "@/services";

/**
 * Página de recuperación de contraseña
 *
 * Permite a los usuarios solicitar un enlace para restablecer
 * su contraseña mediante su correo electrónico.
 * Optimizada para dispositivos móviles con controles táctiles.
 */
const ForgotPasswordPage: React.FC = () => {
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);

  /**
   * Maneja el envío del formulario de recuperación
   */
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (isLoading) return;

    // Validación básica
    if (!email.trim()) {
      setError("Por favor, ingresa tu correo electrónico");
      return;
    }

    // Validación de formato de email
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      setError("Por favor, ingresa un correo electrónico válido");
      return;
    }

    setIsLoading(true);
    setError("");

    try {
      await authService.forgotPassword(email);
      setSuccess(true);
    } catch (error: any) {
      setError(error.message || "Error al enviar el enlace de recuperación");
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * Limpia el error cuando el usuario empieza a escribir
   */
  const handleEmailChange = (value: string) => {
    if (error) {
      setError("");
    }
    setEmail(value);
  };

  return (
    <div className="min-h-screen bg-background flex flex-col">
      {/* Contenido principal - Layout de 2 columnas */}
      <div className="flex-1 flex items-center justify-center p-4 sm:p-6 lg:p-8">
        <div className="w-full max-w-4xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 lg:gap-6">
            
            {/* Columna Izquierda - Formulario */}
            <div className="flex flex-col justify-center">
              {/* Hero Section */}
              <div className="text-center lg:text-left mb-4">
                <Badge className="bg-primary/20 text-primary border-primary/30 px-2 py-1 rounded-full mb-2 text-xs">
                  <Sparkles className="h-2.5 w-2.5 mr-1" />
                  Sistema de Gestión Escolar
                </Badge>
                <h1 className="text-xl sm:text-2xl lg:text-3xl font-bold text-foreground mb-2">
                  Recuperar Contraseña
                </h1>
                <p className="text-sm text-muted-foreground">
                  Ingresa tu correo electrónico para recibir un enlace de recuperación
                </p>
              </div>

              {/* Card de Recuperación */}
              <Card className="shadow-lg hover:shadow-xl transition-all duration-300 border-0 bg-gradient-to-br from-card/80 to-card/60 backdrop-blur-xl">
                <CardHeader className="space-y-1 pb-3">
                  <div className="flex items-center justify-center lg:justify-start mb-2">
                    <div className="p-1.5 bg-gradient-to-br from-primary/20 to-primary/10 rounded-lg border border-primary/20">
                      <Key className="h-5 w-5 text-primary" />
                    </div>
                  </div>
                  <CardTitle className="text-lg font-bold text-center lg:text-left text-foreground">
                    Restablecer Contraseña
                  </CardTitle>
                  <CardDescription className="text-center lg:text-left text-muted-foreground text-xs">
                    Te enviaremos un enlace seguro para cambiar tu contraseña
                  </CardDescription>
                </CardHeader>

                <CardContent className="space-y-3">
                  {!success ? (
                    <form onSubmit={handleSubmit} className="space-y-3">
                      {/* Campo Email */}
                      <div className="space-y-1.5">
                        <Label
                          htmlFor="email"
                          className="text-sm font-medium text-foreground"
                        >
                          Correo Electrónico
                        </Label>
                        <Input
                          id="email"
                          type="email"
                          placeholder="tu@email.com"
                          value={email}
                          onChange={(e) => handleEmailChange(e.target.value)}
                          disabled={isLoading}
                          required
                          className="h-9 text-sm rounded-md border-2 focus:ring-2 focus:ring-primary/20 transition-all duration-200"
                          autoComplete="email"
                        />
                      </div>

                      {/* Mensaje de Error */}
                      {error && (
                        <Alert
                          variant="destructive"
                          className="border-destructive/50"
                        >
                          <AlertDescription className="text-destructive-foreground">
                            {error}
                          </AlertDescription>
                        </Alert>
                      )}

                      {/* Botón de Envío */}
                      <Button
                        type="submit"
                        className="w-full h-9 text-xs font-medium rounded-md bg-gradient-to-r from-primary to-primary/80 hover:from-primary/90 hover:to-primary/70 transition-all duration-200 hover:scale-105 hover:shadow-lg"
                        disabled={isLoading}
                      >
                        {isLoading ? (
                          <>
                            <Loader2 className="mr-2 h-3 w-3 animate-spin" />
                            Enviando enlace...
                          </>
                        ) : (
                          <>
                            <Mail className="mr-2 h-3 w-3" />
                            Enviar Enlace de Recuperación
                          </>
                        )}
                      </Button>
                    </form>
                  ) : (
                    /* Mensaje de Éxito */
                    <div className="space-y-3">
                      <Alert className="border-green-200 bg-green-50/50">
                        <CheckCircle className="h-4 w-4 text-green-600" />
                        <AlertDescription className="text-green-800">
                          Si existe una cuenta asociada a este correo, se ha enviado un enlace para restablecer la contraseña. Por favor, revisa tu bandeja de entrada.
                        </AlertDescription>
                      </Alert>
                      
                      <div className="text-center space-y-2">
                        <p className="text-xs text-muted-foreground">
                          ¿No recibiste el correo? Revisa tu carpeta de spam o{" "}
                          <button
                            onClick={() => setSuccess(false)}
                            className="text-primary hover:text-primary/80 font-medium transition-colors focus:outline-none focus:ring-2 focus:ring-primary/20 rounded"
                          >
                            intenta nuevamente
                          </button>
                        </p>
                      </div>
                    </div>
                  )}

                  {/* Enlaces de Navegación */}
                  <div className="text-center lg:text-left pt-1">
                    <p className="text-xs text-muted-foreground">
                      <Link
                        to="/login"
                        className="inline-flex items-center gap-1 text-primary hover:text-primary/80 font-medium transition-colors focus:outline-none focus:ring-2 focus:ring-primary/20 rounded"
                      >
                        <ArrowLeft className="h-3 w-3" />
                        Volver al inicio de sesión
                      </Link>
                    </p>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Columna Derecha - Información de Ayuda */}
            <div className="flex flex-col justify-center space-y-3">
              
              {/* Sección de Beneficios */}
              <div className="space-y-3">
                <div className="text-center lg:text-left">
                  <h2 className="text-lg lg:text-xl font-bold text-foreground mb-1">
                    ¿Por qué usar este sistema?
                  </h2>
                  <p className="text-xs text-muted-foreground">
                    Recupera tu cuenta de forma segura y rápida
                  </p>
                </div>

                {/* Cards de Beneficios */}
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-1 gap-2">
                  <div className="p-2 bg-gradient-to-r from-primary/10 to-primary/5 rounded-md border border-primary/20 hover:shadow-sm transition-all duration-200">
                    <div className="flex items-start gap-1.5">
                      <div className="p-1 bg-primary/20 rounded-sm">
                        <Shield className="h-3 w-3 text-primary" />
                      </div>
                      <div>
                        <h3 className="font-semibold text-foreground mb-0.5 text-xs">
                          Proceso Seguro
                        </h3>
                        <p className="text-xs text-muted-foreground">
                          Enlaces de recuperación con encriptación de alta seguridad
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className="p-2 bg-gradient-to-r from-green-500/10 to-green-500/5 rounded-md border border-green-500/20 hover:shadow-sm transition-all duration-200">
                    <div className="flex items-start gap-1.5">
                      <div className="p-1 bg-green-500/20 rounded-sm">
                        <CheckCircle2 className="h-3 w-3 text-green-600" />
                      </div>
                      <div>
                        <h3 className="font-semibold text-foreground mb-0.5 text-xs">
                          Recuperación Rápida
                        </h3>
                        <p className="text-xs text-muted-foreground">
                          Recibe el enlace en minutos y restablece tu contraseña
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className="p-2 bg-gradient-to-r from-blue-500/10 to-blue-500/5 rounded-md border border-blue-500/20 hover:shadow-sm transition-all duration-200">
                    <div className="flex items-start gap-1.5">
                      <div className="p-1 bg-blue-500/20 rounded-sm">
                        <Clock className="h-3 w-3 text-blue-600" />
                      </div>
                      <div>
                        <h3 className="font-semibold text-foreground mb-0.5 text-xs">
                          Enlaces Temporales
                        </h3>
                        <p className="text-xs text-muted-foreground">
                          Los enlaces expiran automáticamente por tu seguridad
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className="p-2 bg-gradient-to-r from-purple-500/10 to-purple-500/5 rounded-md border border-purple-500/20 hover:shadow-sm transition-all duration-200">
                    <div className="flex items-start gap-1.5">
                      <div className="p-1 bg-purple-500/20 rounded-sm">
                        <Zap className="h-3 w-3 text-purple-600" />
                      </div>
                      <div>
                        <h3 className="font-semibold text-foreground mb-0.5 text-xs">
                          Acceso Inmediato
                        </h3>
                        <p className="text-xs text-muted-foreground">
                          Una vez restablecida, accede inmediatamente a tu cuenta
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Sección de Instrucciones */}
              <div className="p-3 bg-gradient-to-r from-muted/50 to-muted/30 rounded-md border border-border/50">
                <h3 className="font-semibold text-xs text-foreground mb-2 flex items-center gap-1">
                  <Key className="h-2.5 w-2.5 text-primary" />
                  📧 Pasos para Recuperar
                </h3>
                <div className="space-y-1 text-xs text-muted-foreground">
                  <div className="flex items-start gap-2 p-1 bg-background/50 rounded-sm">
                    <span className="font-medium text-primary">1.</span>
                    <span>Ingresa tu correo electrónico registrado</span>
                  </div>
                  <div className="flex items-start gap-2 p-1 bg-background/50 rounded-sm">
                    <span className="font-medium text-primary">2.</span>
                    <span>Revisa tu bandeja de entrada</span>
                  </div>
                  <div className="flex items-start gap-2 p-1 bg-background/50 rounded-sm">
                    <span className="font-medium text-primary">3.</span>
                    <span>Haz clic en el enlace de recuperación</span>
                  </div>
                  <div className="flex items-start gap-2 p-1 bg-background/50 rounded-sm">
                    <span className="font-medium text-primary">4.</span>
                    <span>Crea una nueva contraseña segura</span>
                  </div>
                </div>
              </div>

              {/* Sección de Seguridad */}
              <div className="p-2 bg-gradient-to-r from-amber-500/10 to-amber-500/5 rounded-md border border-amber-500/20">
                <h4 className="font-medium text-xs mb-1 text-amber-600 flex items-center gap-1">
                  <Lock className="h-2.5 w-2.5" />
                  Protección de Datos
                </h4>
                <ul className="text-xs text-amber-600/80 space-y-0.5">
                  <li>• Enlaces válidos por 1 hora</li>
                  <li>• Encriptación de extremo a extremo</li>
                  <li>• No compartimos tu información</li>
                  <li>• Acceso seguro a tu cuenta</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Botón de regreso al final */}
      <div className="p-4 sm:p-6">
        <Button
          variant="ghost"
          onClick={() => navigate("/")}
          className="flex items-center gap-2 h-7 hover:bg-primary/10 transition-colors text-xs"
        >
          <ArrowLeft className="h-3 w-3" />
          <Store className="h-3 w-3" />
          Volver a la Tienda
        </Button>
      </div>
    </div>
  );
};

export default ForgotPasswordPage; 